import { getPartById } from '../catalog/part-catalog'
import type { PlacedPart } from './assembly'
import type { AssemblyConnection } from './connection'

type StructuralConnection = {
  connectionId: string
  strength: number
  reinforced: boolean
}

export type StructuralPartAnalysis = {
  instanceId: string
  connectionCount: number
  retention: number
  status: 'reinforced' | 'stable' | 'fragile' | 'detached'
}

export type StructuralAnalysis = {
  assemblyScore: number
  connections: StructuralConnection[]
  parts: StructuralPartAnalysis[]
  reinforcedCount: number
  fragilePartIds: string[]
}

export type GroundContactAnalysis = {
  totalMassKg: number
  centerOfMass: { x: number; z: number }
  supportPartIds: string[]
  supportLabels: string[]
  stable: boolean
  bodyScrapeLikely: boolean
  driveEfficiency: number
  frictionReason: string
}

export type ImpactFailure = {
  instanceId: string
  connectionIds: string[]
  retention: number
  separationThresholdJ: number
}

export function connectedAssemblyForPart(
  parts: PlacedPart[],
  connections: AssemblyConnection[],
  instanceId: string | null,
) {
  if (!instanceId) return []
  const partById = new Map(parts.map((part) => [part.instanceId, part]))
  if (!partById.has(instanceId)) return []
  const adjacent = new Map(parts.map((part) => [part.instanceId, new Set<string>()]))
  for (const connection of connections) {
    if (!partById.has(connection.fromPartId) || !partById.has(connection.toPartId)) continue
    adjacent.get(connection.fromPartId)?.add(connection.toPartId)
    adjacent.get(connection.toPartId)?.add(connection.fromPartId)
  }
  const visited = new Set([instanceId])
  const pending = [instanceId]
  while (pending.length) {
    const current = pending.pop()!
    for (const neighbor of adjacent.get(current) ?? []) {
      if (visited.has(neighbor)) continue
      visited.add(neighbor)
      pending.push(neighbor)
    }
  }
  return parts.filter((part) => visited.has(part.instanceId))
}

export function primaryConnectedAssembly(
  parts: PlacedPart[],
  connections: AssemblyConnection[],
) {
  if (!parts.length) return []
  const partById = new Map(parts.map((part) => [part.instanceId, part]))
  const adjacent = new Map(parts.map((part) => [part.instanceId, new Set<string>()]))
  for (const connection of connections) {
    if (!partById.has(connection.fromPartId) || !partById.has(connection.toPartId)) continue
    adjacent.get(connection.fromPartId)?.add(connection.toPartId)
    adjacent.get(connection.toPartId)?.add(connection.fromPartId)
  }
  const visited = new Set<string>()
  const groups: PlacedPart[][] = []
  for (const part of parts) {
    if (visited.has(part.instanceId)) continue
    const ids = [part.instanceId]
    const group: PlacedPart[] = []
    visited.add(part.instanceId)
    while (ids.length) {
      const instanceId = ids.pop()!
      const member = partById.get(instanceId)
      if (member) group.push(member)
      for (const neighbor of adjacent.get(instanceId) ?? []) {
        if (visited.has(neighbor)) continue
        visited.add(neighbor)
        ids.push(neighbor)
      }
    }
    groups.push(group)
  }
  return groups.sort((left, right) => {
    const cubeCount = (group: PlacedPart[]) =>
      group.filter((part) => getPartById(part.partId)?.series === 'CUBE').length
    return cubeCount(right) * 100 + right.length - (cubeCount(left) * 100 + left.length)
  })[0]
}

function canReachWithoutConnection(
  start: string,
  end: string,
  connections: AssemblyConnection[],
  omittedConnectionId: string,
) {
  const adjacent = new Map<string, string[]>()
  for (const connection of connections) {
    if (connection.id === omittedConnectionId) continue
    const from = adjacent.get(connection.fromPartId) ?? []
    from.push(connection.toPartId)
    adjacent.set(connection.fromPartId, from)
    const to = adjacent.get(connection.toPartId) ?? []
    to.push(connection.fromPartId)
    adjacent.set(connection.toPartId, to)
  }
  const visited = new Set([start])
  const pending = [start]
  while (pending.length) {
    const current = pending.pop()!
    if (current === end) return true
    for (const neighbor of adjacent.get(current) ?? []) {
      if (visited.has(neighbor)) continue
      visited.add(neighbor)
      pending.push(neighbor)
    }
  }
  return false
}

export function analyzeStructure(
  parts: PlacedPart[],
  connections: AssemblyConnection[],
): StructuralAnalysis {
  const connectionAnalyses = connections.map((connection) => {
    const reinforced = canReachWithoutConnection(
      connection.fromPartId,
      connection.toPartId,
      connections,
      connection.id,
    )
    const baseStrength = connection.jointType === 'fixed' ? 0.72 : 0.46
    return {
      connectionId: connection.id,
      reinforced,
      strength: Math.min(0.98, baseStrength + (reinforced ? 0.23 : 0)),
    }
  })
  const analysisByConnection = new Map(
    connectionAnalyses.map((analysis) => [analysis.connectionId, analysis]),
  )
  const partAnalyses = parts.map((part): StructuralPartAnalysis => {
    const incident = connections.filter((connection) =>
      connection.fromPartId === part.instanceId || connection.toPartId === part.instanceId)
    if (!incident.length) {
      return {
        instanceId: part.instanceId,
        connectionCount: 0,
        retention: 0,
        status: 'detached',
      }
    }
    const strengths = incident.map((connection) =>
      analysisByConnection.get(connection.id)?.strength ?? 0)
    const strongest = Math.max(...strengths)
    const redundancyBonus = Math.min(0.18, (incident.length - 1) * 0.07)
    const retention = Math.min(0.99, strongest + redundancyBonus)
    return {
      instanceId: part.instanceId,
      connectionCount: incident.length,
      retention,
      status: retention >= 0.9
        ? 'reinforced'
        : retention >= 0.68
          ? 'stable'
          : 'fragile',
    }
  })
  const connectedParts = partAnalyses.filter((part) => part.status !== 'detached')
  const assemblyScore = connectedParts.length
    ? connectedParts.reduce((sum, part) => sum + part.retention, 0) / connectedParts.length
    : 0

  return {
    assemblyScore,
    connections: connectionAnalyses,
    parts: partAnalyses,
    reinforcedCount: connectionAnalyses.filter((connection) => connection.reinforced).length,
    fragilePartIds: partAnalyses
      .filter((part) => part.status === 'fragile')
      .map((part) => part.instanceId),
  }
}

export function findImpactFailure(
  parts: PlacedPart[],
  connections: AssemblyConnection[],
  memberIds: string[],
  impactPoint: { x: number; z: number },
  impactEnergyJ: number,
): ImpactFailure | null {
  const structure = analyzeStructure(parts, connections)
  const members = new Set(memberIds)
  const candidates = structure.parts
    .filter((part) => members.has(part.instanceId) && part.status === 'fragile')
    .map((analysis) => {
      const part = parts.find((item) => item.instanceId === analysis.instanceId)
      const connectionIds = connections
        .filter((connection) =>
          connection.fromPartId === analysis.instanceId
          || connection.toPartId === analysis.instanceId)
        .map((connection) => connection.id)
      return {
        instanceId: analysis.instanceId,
        connectionIds,
        retention: analysis.retention,
        // Provisional calibration: a lone wheel mount releases around 26 mJ,
        // while redundant/stable joints are not eligible for this failure path.
        separationThresholdJ: 0.01 + analysis.retention * 0.035,
        distance: part
          ? Math.hypot(part.position.x - impactPoint.x, part.position.z - impactPoint.z)
          : Number.POSITIVE_INFINITY,
      }
    })
    .filter((candidate) =>
      candidate.connectionIds.length > 0
      && impactEnergyJ >= candidate.separationThresholdJ)
    .sort((left, right) => left.distance - right.distance)

  const failure = candidates[0]
  if (!failure) return null
  return {
    instanceId: failure.instanceId,
    connectionIds: failure.connectionIds,
    retention: failure.retention,
    separationThresholdJ: failure.separationThresholdJ,
  }
}

function isWheel(part: PlacedPart) {
  const definition = getPartById(part.partId)
  return definition?.kind === 'wheel' || definition?.series === 'LINK-WL'
}

function isNarrowFoot(part: PlacedPart) {
  return part.partId === 'part-link-c1-c1-19'
}

export function analyzeGroundContact(parts: PlacedPart[]): GroundContactAnalysis {
  const weighted = parts.reduce((result, part) => {
    const mass = getPartById(part.partId)?.physics.mass ?? 0.024
    return {
      mass: result.mass + mass,
      x: result.x + part.position.x * mass,
      z: result.z + part.position.z * mass,
    }
  }, { mass: 0, x: 0, z: 0 })
  const centerOfMass = {
    x: weighted.mass ? weighted.x / weighted.mass : 0,
    z: weighted.mass ? weighted.z / weighted.mass : 0,
  }
  const wheels = parts.filter(isWheel)
  const feet = parts.filter(isNarrowFoot)
  const supports = [...wheels, ...feet]
  const minX = Math.min(...supports.map((part) => part.position.x))
  const maxX = Math.max(...supports.map((part) => part.position.x))
  const minZ = Math.min(...supports.map((part) => part.position.z))
  const maxZ = Math.max(...supports.map((part) => part.position.z))
  const supportTolerance = 0.08
  const stable = supports.length >= 3
    && centerOfMass.x >= minX - supportTolerance
    && centerOfMass.x <= maxX + supportTolerance
    && centerOfMass.z >= minZ - supportTolerance
    && centerOfMass.z <= maxZ + supportTolerance
  const incompleteDrive = wheels.length < 2
  const bodyScrapeLikely = wheels.length >= 2 && !stable
  const narrowFootPenalty = feet.length ? 0.05 : 0
  const driveEfficiency = wheels.length === 0
    ? 0.05
    : wheels.length === 1
      ? 0.18
      : bodyScrapeLikely
        ? 0.42
        : Math.max(0.72, 0.96 - narrowFootPenalty)

  return {
    totalMassKg: weighted.mass,
    centerOfMass,
    supportPartIds: supports.map((part) => part.instanceId),
    supportLabels: [
      ...(wheels.length ? [`바퀴 ${wheels.length}개`] : []),
      ...(feet.length ? [`C1-19 ${feet.length}개`] : []),
    ],
    stable,
    bodyScrapeLikely,
    driveEfficiency,
    frictionReason: incompleteDrive
      ? wheels.length
        ? '구동 바퀴가 1개뿐이라 직진 안정성과 구동력이 크게 떨어집니다.'
        : '연결된 구동 바퀴가 없어 주행할 수 없습니다.'
      : bodyScrapeLikely
      ? '무게중심이 바퀴 지지선 밖에 있어 전면 몸체가 바닥에 닿는 것으로 추정됩니다.'
      : feet.length
        ? '무게중심이 지지 영역 안에 있으며 보조 지지점이 차체 접촉과 불필요한 마찰을 줄입니다.'
        : '무게중심이 바닥 지지 영역 안에 있어 접지 상태가 안정적입니다.',
  }
}

export function summarizeRobotCondition(
  structure: StructuralAnalysis,
  groundContact: GroundContactAnalysis,
) {
  const detachedCount = structure.parts.length > 1
    ? structure.parts.filter((part) => part.status === 'detached').length
    : 0
  if (detachedCount) {
    return '분리된 부품이 있어 전체 구조가 하나로 작동하지 않습니다. 연결 상태를 먼저 확인하세요.'
  }
  if (structure.assemblyScore < 0.65) {
    return '결합 안정성이 낮고 충격에 취약한 연결이 있습니다. 연결을 보강한 뒤 접지 상태를 다시 확인하세요.'
  }
  if (!groundContact.supportPartIds.length) {
    return '결합 상태는 유지되지만 바닥 지지점과 구동 접점이 부족해 안정적인 주행이 어렵습니다.'
  }
  if (groundContact.bodyScrapeLikely) {
    return '결합 상태는 유지되지만 무게중심이 지지 영역을 벗어나 차체 접촉과 마찰 증가가 예상됩니다.'
  }
  if (!groundContact.stable) {
    return '결합 상태는 양호하지만 바닥 지지 영역이 충분하지 않아 회전하거나 충돌할 때 자세가 불안정할 수 있습니다.'
  }
  if (groundContact.driveEfficiency < 0.7) {
    return '결합과 접지는 유지되지만 구동 접점 구성이 부족해 이동 효율과 직진 안정성이 낮습니다.'
  }
  return '결합 안정성과 바닥 지지 상태가 양호하며 무게중심이 지지 영역 안에 있습니다.'
}
